<template>
    <div>
        <b-form-group :label="typeLabel || `Тип назначения ${participantLabel}`" label-for="participant-type">
            <v-select
                id="participant-type"
                v-model="localConfig.type"
                :options="typeOptions"
                label="text"
                :reduce="option => option.value"
                placeholder="Выберите тип назначения"
                :clearable="false"
                @input="onTypeChange"
            />
        </b-form-group>

        <!-- Конкретные пользователи -->
        <div v-if="localConfig.type === 'specific_users'" class="mt-3">
            <b-form-group label="Выберите пользователей" label-for="specific-users">
                <v-select
                    id="specific-users"
                    v-model="selectedUser"
                    :options="users"
                    label="text"
                    :reduce="option => option.value"
                    placeholder="Выберите пользователя для добавления"
                    @input="addUser"
                />
            </b-form-group>

            <div v-if="localConfig.user_ids && localConfig.user_ids.length" class="mt-2">
                <h6 class="small">Выбранные пользователи:</h6>
                <div
                    v-for="userId in localConfig.user_ids"
                    :key="userId"
                    class="d-flex justify-content-between align-items-center border rounded px-2 py-1 mb-1"
                >
                    <EmployeeLink :user-id="userId" :fallback="getUserName(userId)" compact />
                    <b-button-group size="sm">
                        <b-button
                            v-if="showNotificationMode"
                            :variant="getUserNotificationModeMeta(userId).variant"
                            @click="cycleUserNotificationMode(userId)"
                            :title="getUserNotificationModeMeta(userId).title"
                        >
                            <i :class="getUserNotificationModeMeta(userId).icon"></i>
                        </b-button>
                        <b-button variant="outline-danger" @click="removeUser(userId)">
                            <i class="fas fa-times"></i>
                        </b-button>
                    </b-button-group>
                </div>
            </div>
            <div v-else class="text-muted small">
                Пользователи не выбраны
            </div>
        </div>

        <!-- Отдел -->
        <div v-if="localConfig.type === 'department'" class="mt-3">
            <b-form-group label="Выберите отдел" label-for="department">
                <v-select
                    id="department"
                    v-model="localConfig.department_id"
                    :options="departments"
                    label="text"
                    :reduce="option => option.value"
                    placeholder="Выберите отдел"
                    :clearable="false"
                    @input="emitUpdate"
                />
            </b-form-group>

            <!-- Опция для исполнителя: требуется назначение начальником отдела -->
            <div v-if="showDepartmentHead">
                <b-form-checkbox
                    v-model="localConfig.requires_head_assignment"
                    @change="emitUpdate"
                    switch
                >
                    Требуется назначение конкретного исполнителя начальником отдела
                </b-form-checkbox>
                <small class="text-muted d-block mt-1">
                    Если включено, начальник отдела должен будет назначить конкретного сотрудника на заявку
                </small>
            </div>
        </div>

        <!-- Роль на объекте -->
        <div v-if="localConfig.type === 'object_role'" class="mt-3">
            <b-form-group label="Роль на объекте" label-for="object-role">
                <b-form-checkbox-group
                    id="object-role"
                    v-model="localConfig.roles"
                    @change="emitUpdate"
                >
                    <b-form-checkbox
                        v-for="role in resolvedObjectRoleOptions"
                        :key="role.value"
                        :value="role.value"
                    >
                        {{ role.text }}
                    </b-form-checkbox>
                </b-form-checkbox-group>
            </b-form-group>
            <small class="text-muted">
                {{ objectRoleHintText || 'Роль будет определяться на основе строительного процесса, указанного в заявке' }}
            </small>
        </div>

        <!-- Роль в заявке -->
        <div v-if="localConfig.type === 'request_role'" class="mt-3">
            <b-form-group label="Роль в заявке" label-for="request-role">
                <b-form-checkbox-group
                    id="request-role"
                    v-model="localConfig.roles"
                    @change="emitUpdate"
                >
                    <b-form-checkbox value="initiator">Инициатор</b-form-checkbox>
                    <b-form-checkbox value="approver">Согласующий</b-form-checkbox>
                    <b-form-checkbox value="assigner">Назначающий</b-form-checkbox>
                    <b-form-checkbox value="executor">Исполнитель</b-form-checkbox>
                    <b-form-checkbox value="observer">Наблюдатель</b-form-checkbox>
                </b-form-checkbox-group>
            </b-form-group>
            <small class="text-muted">
                Пользователь будет определяться по участникам конкретной заявки
            </small>
        </div>

        <!-- Все пользователи -->
        <div v-if="localConfig.type === 'all'" class="mt-3">
            <b-alert show variant="info" class="mb-0">
                <i class="fas fa-info-circle"></i>
                {{ allText || `Любой пользователь системы может быть ${participantLabel}` }}
            </b-alert>
        </div>
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
const DEFAULT_NOTIFICATION_MODE = 'default';
const NOTIFICATION_MODE_SEQUENCE = ['default', 'always', 'never'];
const NOTIFICATION_MODE_META = {
    always: {
        icon: 'fas fa-bell',
        variant: 'outline-success',
        title: 'Уведомления: всегда отправлять'
    },
    never: {
        icon: 'fas fa-bell-slash',
        variant: 'outline-danger',
        title: 'Уведомления: не отправлять'
    },
    default: {
        icon: 'fas fa-bell',
        variant: 'outline-secondary',
        title: 'Уведомления: по настройкам типа заявки'
    }
};

const DEFAULT_OBJECT_ROLE_OPTIONS = [
    { value: 'curator', text: 'Куратор' },
    { value: 'main_staff', text: 'Прораб' },
    { value: 'pto', text: 'ПТО' },
    { value: 'has_access', text: 'Имеющий доступ' }
];

function cloneConfig(value) {
    return JSON.parse(JSON.stringify(value || { type: 'all' }));
}

export default {
    components: {EmployeeLink},
    name: "ParticipantConfig",
    props: {
        value: {
            type: Object,
            default: () => ({ type: 'all' })
        },
        users: {
            type: Array,
            default: () => []
        },
        departments: {
            type: Array,
            default: () => []
        },
        participantLabel: {
            type: String,
            default: 'участника'
        },
        showObjectRoles: {
            type: Boolean,
            default: false
        },
        showRequestRoles: {
            type: Boolean,
            default: false
        },
        showDepartmentHead: {
            type: Boolean,
            default: false
        },
        typeLabel: {
            type: String,
            default: null
        },
        allText: {
            type: String,
            default: null
        },
        showNotificationMode: {
            type: Boolean,
            default: false
        },
        allowedTypes: {
            type: Array,
            default: () => null
        },
        objectRoleOptions: {
            type: Array,
            default: () => DEFAULT_OBJECT_ROLE_OPTIONS.map(option => ({ ...option }))
        },
        objectRoleHintText: {
            type: String,
            default: null
        }
    },
    data() {
        const initialConfig = this.normalizeConfig(this.value || { type: 'all' });
        
        return {
            localConfig: initialConfig,
            selectedUser: null
        }
    },
    computed: {
        typeOptions() {
            const baseOptions = [
                { value: 'all', text: 'Все пользователи' },
                { value: 'specific_users', text: 'Конкретные пользователи' },
                { value: 'department', text: 'Отдел' }
            ];

            if (this.showObjectRoles) {
                baseOptions.push({
                    value: 'object_role',
                    text: 'Роль на объекте'
                });
            }

            if (this.showRequestRoles) {
                baseOptions.push({
                    value: 'request_role',
                    text: 'Роль в заявке'
                });
            }

            return this.allowedTypes && this.allowedTypes.length
                ? baseOptions.filter(option => this.allowedTypes.includes(option.value))
                : baseOptions;
        },
        resolvedObjectRoleOptions() {
            return Array.isArray(this.objectRoleOptions) && this.objectRoleOptions.length
                ? this.objectRoleOptions
                : DEFAULT_OBJECT_ROLE_OPTIONS.map(option => ({ ...option }));
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal) {
                    this.localConfig = this.normalizeConfig(newVal);
                } else {
                    this.localConfig = { type: 'all' };
                }
            },
            deep: true
        }
    },
    methods: {
        onTypeChange() {
            // Сброс специфичных полей при смене типа
            const nextType = this.localConfig.type;
            const preserved = {};
            ['_uid', 'priority'].forEach(key => {
                if (this.localConfig[key] !== undefined) {
                    preserved[key] = this.localConfig[key];
                }
            });
            this.localConfig = { ...preserved, type: nextType };
            
            if (this.localConfig.type === 'specific_users') {
                this.localConfig.user_ids = [];
                if (this.showNotificationMode) {
                    this.localConfig.notification_modes = {};
                }
            } else if (this.localConfig.type === 'department') {
                this.localConfig.department_id = null;
                if (this.showDepartmentHead) {
                    this.localConfig.requires_head_assignment = false;
                }
            } else if (this.localConfig.type === 'object_role') {
                this.localConfig.roles = [];
            } else if (this.localConfig.type === 'request_role') {
                this.localConfig.roles = [];
            }
            
            this.emitUpdate();
        },
        addUser() {
            if (!this.selectedUser) return;
            
            if (!this.localConfig.user_ids) {
                this.localConfig.user_ids = [];
            }
            
            if (!this.localConfig.user_ids.includes(this.selectedUser)) {
                this.localConfig.user_ids.push(this.selectedUser);
                if (this.showNotificationMode) {
                    if (!this.localConfig.notification_modes) {
                        this.$set(this.localConfig, 'notification_modes', {});
                    }
                    this.$set(this.localConfig.notification_modes, String(this.selectedUser), 'default');
                }
                this.emitUpdate();
            }
            
            this.selectedUser = null;
        },
        removeUser(userId) {
            const index = this.localConfig.user_ids.indexOf(userId);
            if (index !== -1) {
                this.localConfig.user_ids.splice(index, 1);
                if (this.localConfig.notification_modes) {
                    this.$delete(this.localConfig.notification_modes, String(userId));
                }
                this.emitUpdate();
            }
        },
        getUserName(userId) {
            const user = this.users.find(u => u.value === userId);
            return user ? user.text : `ID: ${userId}`;
        },
        emitUpdate() {
            this.$emit('input', this.normalizeConfig(this.localConfig));
        },
        normalizeConfig(value) {
            const config = cloneConfig(value);
            if (config.type === 'object_role' && !config.roles) {
                config.roles = [];
            }
            if (config.type === 'request_role' && !config.roles) {
                config.roles = [];
            }

            if (!this.showNotificationMode || config.type !== 'specific_users') {
                delete config.notification_modes;
                delete config.notification_mode;
                return config;
            }

            const userIds = Array.isArray(config.user_ids) ? config.user_ids : [];
            const existingModes = config.notification_modes && typeof config.notification_modes === 'object'
                ? config.notification_modes
                : {};
            const fallbackMode = this.normalizeNotificationMode(config.notification_mode);
            const notificationModes = {};

            userIds.forEach(userId => {
                const key = String(userId);
                notificationModes[key] = this.normalizeNotificationMode(existingModes[key] || fallbackMode);
            });

            config.notification_modes = notificationModes;
            delete config.notification_mode;
            return config;
        },
        normalizeNotificationMode(value) {
            return NOTIFICATION_MODE_SEQUENCE.indexOf(value) !== -1
                ? value
                : DEFAULT_NOTIFICATION_MODE;
        },
        getUserNotificationMode(userId) {
            const modes = this.localConfig.notification_modes || {};
            return this.normalizeNotificationMode(modes[String(userId)]);
        },
        cycleUserNotificationMode(userId) {
            if (!this.localConfig.notification_modes) {
                this.$set(this.localConfig, 'notification_modes', {});
            }

            const current = this.getUserNotificationMode(userId);
            const currentIndex = NOTIFICATION_MODE_SEQUENCE.indexOf(current);
            const nextMode = NOTIFICATION_MODE_SEQUENCE[(currentIndex + 1) % NOTIFICATION_MODE_SEQUENCE.length];

            this.$set(this.localConfig.notification_modes, String(userId), nextMode);
            this.emitUpdate();
        },
        getUserNotificationModeMeta(userId) {
            const mode = this.getUserNotificationMode(userId);
            return NOTIFICATION_MODE_META[mode] || NOTIFICATION_MODE_META[DEFAULT_NOTIFICATION_MODE];
        }
    }
}
</script>

<style scoped>
/* Стили для vue-select совместимость с Bootstrap Vue */
:deep(.v-select) {
    min-height: 38px;
}

:deep(.v-select .vs__dropdown-toggle) {
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    min-height: 38px;
}

:deep(.v-select .vs__dropdown-toggle:focus-within) {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

:deep(.v-select .vs__search) {
    margin: 0;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
}

:deep(.v-select .vs__selected-options) {
    padding: 0.375rem 0.75rem;
}
</style>
